// eslint-disable no-use-before-define
import React, { useState, FC } from 'react';
import { useMediaQuery } from '@mui/system';
import { Box, Link } from '@mui/material';
import { Webhook } from '@mui/icons-material';

import WebhookModal from './components/WebhookModal';
import { EmptyPage } from '../../common/EmptyPage';
import { WebhookEndpoint } from './types/types';
import { useWebhooksService } from '../../hooks';
import SubscriptionsTab from './components/SubscriptionsTab';
import EventLogsTab from './components/EventLogsTab';
import { useMerchant } from '../../contexts/MerchantContext';
import PageWrapper from '../../common/PageWrapper';
import theme from '../../styles/themes/theme';
import { MobileSubTabBar } from '../../common/SubTabBar/MobileSubTabBar';
import { SubTabBar } from '../../common/SubTabBar';
import { webhooksDocumentationUrl } from '../../util/constants';
import { PARENT_HIERARCHY_WEBHOOKS_BANNER_MESSAGE } from '../../util/parentSettingSave';

type MerchantWebhooksProps = {
  dataTestId?: string;
};

export const MerchantWebhooks: FC<MerchantWebhooksProps> = (props) => {
  const { dataTestId } = props;
  const [openModal, setOpenModal] = useState(false);
  const [webhookToEdit, setWebhookToEdit] = useState<WebhookEndpoint>();
  const { merchantId } = useMerchant();

  const {
    webhooks,
    isLoading: webhooksLoading,
    fetchWebhooks,
  } = useWebhooksService({ merchantId, skipEventsFetch: true });

  const onAddClick = () => {
    setOpenModal(true);
  };

  const onCloseModal = () => {
    setOpenModal(false);
    setWebhookToEdit(undefined);
  };

  const onEdit = (webhookSubscription: WebhookEndpoint) => {
    setWebhookToEdit(webhookSubscription);
    setOpenModal(true);
  };

  const [selectedTab, setSelectedTab] = useState(0);
  const onSetSelectedTab = (tabIndex: number) => {
    setSelectedTab(tabIndex);
    fetchWebhooks();
  };

  const isEmpty = webhooks.length === 0;

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const TabBar = isMobile ? MobileSubTabBar : SubTabBar;

  return (
    <PageWrapper
      showParentHierarchyBanner
      parentHierarchyBannerMessage={PARENT_HIERARCHY_WEBHOOKS_BANNER_MESSAGE}
      data-testid={dataTestId}
      title="Webhooks"
      description={
        <>
          Create new webhook subscriptions in the Subscriptions tab and view the status of the ones
          that have been sent in the Event Logs tab.{' '}
          <Link
            href={webhooksDocumentationUrl}
            target="_blank"
            rel="noopener noreferrer"
            color="link.main"
            underline="always"
          >
            Learn more about webhooks
          </Link>
          .
        </>
      }
      isLoading={webhooksLoading}
      withDivider={false}
    >
      <Box sx={{ width: '100%', mt: 4 }}>
        <TabBar
          tabs={[
            { id: 'subscriptions', label: 'Subscriptions' },
            { id: 'eventLogs', label: 'Event Logs' },
          ]}
          selectedTab={selectedTab}
          setSelectedTab={onSetSelectedTab}
          ariaLabel="Webhook Tabs"
        />
        {selectedTab === 0 &&
          (isEmpty ? (
            <EmptyPage
              title="You haven't set up any webhooks yet."
              paragraph="By setting up webhooks, you will be able to subscribe to events that will push out information to your server when they are triggered. Get started with your first webhook by clicking 'Add Subscription' below."
              buttonText="Add Subscription"
              icon={<Webhook />}
              onClick={onAddClick}
            />
          ) : (
            <SubscriptionsTab
              webhooks={webhooks}
              onAddWebhookClick={onAddClick}
              onEditWebhook={onEdit}
            />
          ))}
        {selectedTab === 1 && <EventLogsTab merchantId={merchantId} webhooks={webhooks} />}
        {openModal && (
          <WebhookModal
            merchantId={merchantId}
            isOpen={openModal}
            onClose={onCloseModal}
            dataTestId={`${dataTestId}-WebhookModal`}
            webhookToEdit={webhookToEdit}
          />
        )}
      </Box>
    </PageWrapper>
  );
};
